import { useState } from 'react';
import { X, Play, Loader2, Zap } from 'lucide-react';

const FLAGSHIP_WORKFLOWS = [
  {
    id: 'weekly_retail_operating_review',
    name: 'Weekly Retail Operating Review',
    description: 'Comprehensive weekly performance analysis with actionable recommendations',
    requiresRetailer: false
  },
  {
    id: 'root_cause_sales_delta',
    name: 'Root Cause: Sales Delta',
    description: 'Analyze why sales changed and identify contributing factors',
    requiresRetailer: true
  },
  {
    id: 'buyer_meeting_pack',
    name: 'Buyer Meeting Pack',
    description: 'Generate a complete briefing pack for retailer buyer meetings',
    requiresRetailer: true
  }
] as const;

type Retailer = {
  id: string;
  name: string;
};

interface RunWorkflowModalProps {
  isOpen: boolean;
  onClose: () => void;
  onRun: (workflowId: string, retailerId?: string) => Promise<void>;
  retailers: Retailer[];
  isRunning: boolean;
}

export default function RunWorkflowModal({
  isOpen,
  onClose,
  onRun,
  retailers,
  isRunning
}: RunWorkflowModalProps) {
  const [selectedWorkflow, setSelectedWorkflow] = useState<string>(FLAGSHIP_WORKFLOWS[0].id);
  const [selectedRetailer, setSelectedRetailer] = useState<string>('');
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  const workflow = FLAGSHIP_WORKFLOWS.find(w => w.id === selectedWorkflow);
  const showRetailerDropdown = workflow?.requiresRetailer && retailers.length > 0;

  const handleRun = async () => {
    setError(null);
    
    // Validate retailer selection if required
    if (workflow?.requiresRetailer && retailers.length > 0 && !selectedRetailer) {
      setError('Please select a retailer for this workflow');
      return;
    }

    try {
      await onRun(selectedWorkflow, selectedRetailer || undefined);
      // Modal will be closed by parent on success
    } catch (e: any) {
      setError(e?.message || 'Failed to run workflow');
    }
  };

  const handleClose = () => {
    if (!isRunning) {
      setError(null);
      onClose();
    }
  };

  return (
    <>
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/40 z-50" 
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div 
          className="bg-white rounded-xl shadow-2xl w-full max-w-md overflow-hidden"
          onClick={e => e.stopPropagation()}
        >
          {/* Header */}
          <div className="px-6 py-4 bg-gradient-to-r from-violet-500 to-purple-600 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-white/20 rounded-lg">
                <Zap className="w-5 h-5 text-white" />
              </div>
              <div>
                <h2 className="text-lg font-bold text-white">Run Workflow</h2>
                <p className="text-violet-100 text-sm">Execute a flagship AI workflow</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={isRunning}
              className="p-2 hover:bg-white/10 rounded-lg disabled:opacity-50"
            >
              <X className="w-5 h-5 text-white" />
            </button>
          </div>

          {/* Body */}
          <div className="p-6 space-y-5">
            {/* Workflow Selection */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Workflow
              </label>
              <select
                value={selectedWorkflow}
                onChange={e => {
                  setSelectedWorkflow(e.target.value);
                  setError(null);
                }}
                disabled={isRunning}
                className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
              >
                {FLAGSHIP_WORKFLOWS.map(wf => (
                  <option key={wf.id} value={wf.id}>
                    {wf.name}
                  </option>
                ))}
              </select>
              {workflow && (
                <p className="mt-2 text-sm text-gray-500">{workflow.description}</p>
              )}
            </div>

            {/* Retailer Selection (conditional) */}
            {showRetailerDropdown && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Retailer
                  <span className="text-red-500 ml-1">*</span>
                </label>
                <select
                  value={selectedRetailer}
                  onChange={e => {
                    setSelectedRetailer(e.target.value);
                    setError(null);
                  }}
                  disabled={isRunning}
                  className="w-full px-4 py-3 border border-gray-200 rounded-lg bg-white focus:ring-2 focus:ring-violet-500 focus:border-violet-500 disabled:opacity-50"
                >
                  <option value="">Select a retailer...</option>
                  {retailers.map(r => (
                    <option key={r.id} value={r.id}>
                      {r.name}
                    </option>
                  ))}
                </select>
              </div>
            )}

            {/* Time Window Info */}
            <div className="bg-gray-50 rounded-lg p-4">
              <div className="text-sm font-medium text-gray-700 mb-1">Time Window</div>
              <div className="text-sm text-gray-500">
                Uses the most recent closed fiscal week automatically.
              </div>
            </div>

            {/* Error */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
                {error}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex items-center justify-end gap-3">
            <button
              onClick={handleClose}
              disabled={isRunning}
              className="px-4 py-2 text-gray-700 hover:bg-gray-100 rounded-lg text-sm font-medium disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={handleRun}
              disabled={isRunning}
              className="px-5 py-2 bg-violet-600 hover:bg-violet-700 text-white rounded-lg text-sm font-semibold flex items-center gap-2 disabled:opacity-60"
            >
              {isRunning ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Running...
                </>
              ) : (
                <>
                  <Play className="w-4 h-4" />
                  Run Workflow
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

